import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { AlertService } from '../alert.service';
import { ALERT_ON_NOT_FOUND, SKIP_ERROR_ALERT, apiErrorMessage } from '../../shared/api/api-error';

/**
 * Alerts once for every failed HTTP request, so a failure can never be silent.
 *
 * <p>Before this there was no error interceptor at all: 29 services each carried their own
 * copy-pasted `handleError`, and the rest of the app subscribed without an error handler, so a
 * failed request could leave a screen sitting on a spinner with nothing said.</p>
 *
 * <p>It <b>rethrows</b>. Swallowing would mean a component's `next` never runs and a dialog stays
 * open waiting for a save that already failed; rethrowing lets callers that need to react still do
 * so, and callers that do not have already had the user told.</p>
 *
 * <p>Registered before <c>MsalInterceptor</c>, which puts it on the outside of the response
 * pipeline: MSAL gets to deal with a token failure first, and this only sees what survives.</p>
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private alertService: AlertService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // TODO: remove once the Result/CQRS controller refactor is done. Routes are still moving
        // around mid-refactor, so 404s are currently mostly noise rather than something a user needs
        // to see; log them for debugging instead of alerting. A request that opts in with
        // ALERT_ON_NOT_FOUND (a converted write, where 404 is a real answer) still alerts.
        if (error.status === 404 && !request.context.get(ALERT_ON_NOT_FOUND)) {
          console.warn('[ErrorInterceptor] 404 suppressed during refactor:', request.url);
        } else if (!request.context.get(SKIP_ERROR_ALERT)) {
          // keepAfterRouteChange, because AlertService clears the stack on NavigationStart: a
          // handler that reacts to the failure by navigating would otherwise wipe this message
          // before anyone read it.
          this.alertService.error(apiErrorMessage(error), true);
        }

        return throwError(() => error);
      })
    );
  }
}
